import { memo, useRef } from 'react';
import { MoreHorizontal, Pin, Reply, SmilePlus } from 'lucide-react';
import { format } from 'date-fns';
import {
  DISCUSSION_REACTIONS,
  DISCUSSION_REACTION_IDS,
  type DiscussionMessage,
  type DiscussionReactionId,
} from '@allemnii-pulse/shared';
import { asDate } from '../../lib/time.js';
import { cn } from '../../lib/utils.js';
import type { UserProfile } from '../../lib/userProfiles.js';
import { UserName } from '../../components/UserName.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover.js';
import { Attachments, MessageBody, mentionName, plainExcerpt } from './MessageBody.js';

const LONG_PRESS_MS = 450;

/** Fires `onLongPress` after holding a finger still on the element — the phone gesture for a
 * message's actions. Movement past a few pixels is a scroll, not a press. Android also raises a
 * contextmenu event for a long press, and desktop for a right-click; both open the same sheet. */
function useLongPress(onLongPress: () => void) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  };
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        fired.current = true;
        navigator.vibrate?.(10);
        onLongPress();
        cancel();
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 8)
        cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      if (!fired.current) onLongPress();
      fired.current = false;
    },
  };
}

export type PendingState = 'sending' | 'failed';

export interface MessageItemProps {
  message: DiscussionMessage;
  myUid: string;
  /** Same author as the message right above, within a few minutes — drawn without the header. */
  grouped: boolean;
  /** The replied-to message: undefined while unknown, null once known to be deleted. */
  parent: DiscussionMessage | null | undefined;
  profiles: Record<string, UserProfile>;
  urls: Record<string, string>;
  highlighted: boolean;
  pending?: PendingState;
  onOpenActions: (message: DiscussionMessage) => void;
  onReply: (message: DiscussionMessage) => void;
  onReact: (message: DiscussionMessage, reaction: DiscussionReactionId) => void;
  onJumpTo: (messageId: string) => void;
  onRetry?: () => void;
  onDiscard?: () => void;
}

/** Reaction chips under a message: tap to add or remove your own. */
function Reactions({
  message,
  myUid,
  profiles,
  onReact,
}: Pick<MessageItemProps, 'message' | 'myUid' | 'profiles' | 'onReact'>) {
  const present = DISCUSSION_REACTION_IDS.filter((id) => (message.reactions[id]?.length ?? 0) > 0);
  if (present.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {present.map((id) => {
        const uids = message.reactions[id] ?? [];
        const mine = uids.includes(myUid);
        const names = uids.map((uid) => (uid === myUid ? 'You' : mentionName(uid, profiles[uid])));
        return (
          <button
            key={id}
            type="button"
            title={names.join(', ')}
            aria-label={`${DISCUSSION_REACTIONS[id]} ${uids.length}, ${mine ? 'remove your reaction' : 'react'}`}
            aria-pressed={mine}
            onClick={() => onReact(message, id)}
            className={cn(
              'flex h-8 items-center gap-1 rounded-full border px-2.5 text-sm tabular-nums transition-colors md:h-7',
              mine
                ? 'border-accent bg-accent-soft text-accent-soft-fg'
                : 'border-border bg-surface hover:bg-surface-2',
            )}
          >
            <span className="text-[15px] leading-none">{DISCUSSION_REACTIONS[id]}</span>
            <span className="text-xs font-medium">{uids.length}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The quoted message a reply points at; tapping it scrolls there. Hidden once that message is
 * deleted — a deleted message leaves nothing behind, quotes included. */
function ReplyQuote({
  parent,
  onJumpTo,
}: {
  parent: DiscussionMessage;
  onJumpTo: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onJumpTo(parent.id)}
      className="mb-1 flex w-full max-w-md items-start gap-1.5 rounded-md border-l-2 border-border bg-surface-2 px-2 py-1 text-left text-xs text-muted hover:border-accent"
    >
      <Reply className="mt-0.5 h-3 w-3 shrink-0 -scale-x-100" />
      <span className="min-w-0 flex-1">
        <UserName uid={parent.authorUid} showStatus={false} className="font-medium text-text" />
        <span className="block truncate">{plainExcerpt(parent)}</span>
      </span>
    </button>
  );
}

/**
 * One message in the discussion. Slack-style rows (avatar, name, time, then the body) rather than
 * chat bubbles, so long messages, attachments and quotes read well at every width. Actions: long
 * press on a phone; a hover toolbar on desktop.
 */
export const MessageItem = memo(function MessageItem(props: MessageItemProps) {
  const { message, myUid, grouped, parent, profiles, urls, highlighted, pending } = props;
  const longPress = useLongPress(() => !pending && props.onOpenActions(message));
  const created = asDate(message.createdAt);
  const time = format(created, 'HH:mm');

  return (
    <div
      id={`msg-${message.id}`}
      {...longPress}
      className={cn(
        'group relative flex gap-2.5 px-3 transition-colors duration-700 sm:px-5',
        grouped ? 'pt-0.5' : 'pt-3',
        'pb-0.5',
        highlighted ? 'bg-accent-soft' : 'md:hover:bg-surface-2/60',
        pending && 'opacity-60',
        // A long press opens the action sheet; the OS's own text-selection callout would fight
        // it. Copying is offered in the sheet instead.
        'max-md:select-none max-md:[-webkit-touch-callout:none]',
      )}
    >
      <div className="w-9 shrink-0">
        {grouped ? (
          <span className="hidden pt-1 text-right text-[10px] tabular-nums text-muted md:group-hover:block">
            {time}
          </span>
        ) : (
          <UserName uid={message.authorUid} avatar hideName avatarClassName="h-9 w-9" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {message.pinnedAt && (
          <div className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-warning">
            <Pin className="h-3 w-3" /> Pinned
          </div>
        )}
        {!grouped && (
          <div className="flex items-baseline gap-2">
            <UserName
              uid={message.authorUid}
              showStatus={false}
              className="text-sm font-semibold"
            />
            <time
              dateTime={created.toISOString()}
              title={format(created, 'PPpp')}
              className="text-xs text-muted"
            >
              {time}
            </time>
          </div>
        )}
        {message.replyToId && parent && <ReplyQuote parent={parent} onJumpTo={props.onJumpTo} />}
        <MessageBody
          body={message.body}
          mentionedUids={message.mentionedUids}
          mentionsAll={message.mentionsAll}
          myUid={myUid}
          profiles={profiles}
        />
        {message.editedAt && <span className="text-[11px] text-muted">(edited)</span>}
        <Attachments attachments={message.attachments} urls={urls} />
        <Reactions message={message} myUid={myUid} profiles={profiles} onReact={props.onReact} />

        {pending === 'sending' && <p className="mt-0.5 text-[11px] text-muted">Sending…</p>}
        {pending === 'failed' && (
          <p className="mt-0.5 flex items-center gap-3 text-xs text-danger">
            Not sent.
            <button type="button" className="font-medium underline" onClick={props.onRetry}>
              Retry
            </button>
            <button type="button" className="text-muted underline" onClick={props.onDiscard}>
              Discard
            </button>
          </p>
        )}
      </div>

      {!pending && (
        <div className="absolute -top-3 right-4 hidden items-center gap-0.5 rounded-lg border border-border bg-surface p-0.5 shadow-sm md:group-hover:flex md:group-focus-within:flex">
          <Popover>
            <PopoverTrigger
              aria-label="Add a reaction"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-text"
            >
              <SmilePlus className="h-4 w-4" />
            </PopoverTrigger>
            <PopoverContent align="end" className="flex gap-0.5 p-1">
              {DISCUSSION_REACTION_IDS.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-label={`React ${DISCUSSION_REACTIONS[id]}`}
                  onClick={() => props.onReact(message, id)}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-lg hover:bg-surface-2"
                >
                  {DISCUSSION_REACTIONS[id]}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <button
            type="button"
            aria-label="Reply"
            onClick={() => props.onReply(message)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-text"
          >
            <Reply className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="More actions"
            onClick={() => props.onOpenActions(message)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-text"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
});
